import React from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MailIcon from '@mui/icons-material/Mail';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import TranslateIcon from '@mui/icons-material/Translate';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { getPhoneNumberUri, toNational } from '~/utils/phone-number-helpers';
import { useIntl } from 'react-intl';
import PopoverButton from '../PopoverButton';
import LanguageName from '../LanguageName';
import PublisherExperienceTag, {
  isValidExperienceLevel,
} from '../PublisherExperienceTag';
import messages from './messages';
import moment from 'moment';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import Skeleton from '@mui/material/Skeleton';

type PublisherCardProps = {
  publisher: {
    email: string;
    firstName: string;
    middleName?: string;
    id: number;
    languages: string[];
    lastName: string;
    phone: string;
    experienceLevel?: string;
    birthDate?: string;
    gender?: string;
  };
  children: React.ComponentProps<typeof PopoverButton>['children'];
};

const PublisherName = (props: {
  firstName: string;
  middleName?: string;
  lastName: string;
}) => <>{[props.firstName, props.middleName, props.lastName].join(' ')}</>;

const GenderIcon = ({ gender }: { gender?: string }) => {
  switch (gender) {
    case 'Male':
      return <MaleIcon />;
    case 'Female':
      return <FemaleIcon />;
    default:
      return <PersonIcon />;
  }
};

const PublisherCard = (props: PublisherCardProps) => {
  const intl = useIntl();

  const { data: masterDataSettings, isFetching: isMasterDataSettingsFetching } =
    useSettingsByCategory('masterdata');

  return (
    <PopoverButton
      id={props.publisher.email}
      content={
        <Card variant="outlined">
          {isMasterDataSettingsFetching ? (
            <Skeleton variant="text" width="100%" height={24} />
          ) : (
            <List>
              <ListItem>
                <ListItemIcon>
                  <GenderIcon gender={props.publisher.gender} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Grid container spacing={1}>
                      <Grid size="grow">
                        <PublisherName
                          firstName={props.publisher.firstName}
                          lastName={props.publisher.lastName}
                          middleName={props.publisher.middleName}
                        />
                      </Grid>
                      <Grid>
                        {props.publisher.experienceLevel &&
                        isValidExperienceLevel(
                          props.publisher.experienceLevel
                        ) ? (
                          <PublisherExperienceTag
                            experienceLevel={props.publisher.experienceLevel}
                          />
                        ) : null}
                      </Grid>
                    </Grid>
                  }
                />
              </ListItem>
              <ListItemButton
                component="a"
                href={`mailto:${props.publisher.email}`}
              >
                <ListItemIcon>
                  <MailIcon />
                </ListItemIcon>
                <ListItemText primary={props.publisher.email} />
              </ListItemButton>
              <ListItem
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={intl.formatMessage(messages.contactViaWhatsApp)}
                    href={`https://wa.me/${props.publisher.phone}`}
                    size="large"
                  >
                    <WhatsAppIcon />
                  </IconButton>
                }
              >
                <ListItemButton
                  component="a"
                  href={getPhoneNumberUri(props.publisher.phone)}
                >
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={toNational(
                      props.publisher.phone,
                      masterDataSettings?.defaultRegionCallingCode as string
                    )}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TranslateIcon />
                </ListItemIcon>
                <ListItemText
                  primary={props.publisher.languages.map((language, index) => (
                    <React.Fragment key={language}>
                      {index > 0 ? ', ' : null}
                      <LanguageName languageCode={language} />
                    </React.Fragment>
                  ))}
                />
              </ListItem>
              {props.publisher.birthDate && (
                <ListItem>
                  <ListItemIcon>
                    <CalendarTodayIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={intl.formatMessage(messages.yearsOld, {
                      age: moment().diff(
                        moment(props.publisher.birthDate),
                        'years'
                      ),
                    })}
                  />
                </ListItem>
              )}
            </List>
          )}
        </Card>
      }
    >
      {props.children}
    </PopoverButton>
  );
};

export default PublisherCard;
